// The donor tote adapter surface — the exact contract og-v2-frontend's
// tote-v2.js/ui-v2.js consume (getSnapshot/subscribe/commands/clear from
// tote.js) — implemented over HRW state instead of the casino round
// protocol. Field, pricing basis (rtpBp) and lock state are pushed in from
// the racecard/market queries; displayed odds price through the same copied
// exact pricer the backend prices with, and the authoritative acceptance
// still happens server-side at placement time.

import { normalizeRaceField } from "@renderer/horse-appearance.js";
import {
  BET_TYPES,
  betOddsH,
  canonicalSel,
  ticketKey,
} from "@renderer/odds.js";

export type ToteHorse = {
  gate: number;
  name: string;
  weight: number;
  silk: string;
} & Record<string, unknown>;

export type ToteField = { horses: ToteHorse[] } | null;

export type ToteLine = {
  key: string;
  type: string;
  sel: number[];
  amount: number;
  oddsH: number;
  groupId?: string;
  groupLabel?: string;
};

export type ToteSnapshot = Readonly<{
  revision: number;
  field: ToteField;
  rtpBp: number;
  betType: string;
  picks: readonly number[];
  lines: readonly ToteLine[];
  total: number;
  locked: boolean;
  mode: "local";
  maxBets: number;
}>;

export type ToteSubscriber = (snapshot: ToteSnapshot, reason: string) => void;

export type ToteAdapter = ReturnType<typeof createHrwTote>;

const DEFAULT_MAX_BETS = 64;

export function createHrwTote({
  getChip = () => 100,
}: { getChip?: () => number } = {}) {
  const state = {
    field: null as ToteField,
    rtpBp: 9600,
    betType: "win",
    picks: [] as number[],
    lines: new Map<
      string,
      { type: string; sel: number[]; amount: number; oddsH: number }
    >(),
    groups: new Map<string, { groupId: string; groupLabel: string }>(),
    locked: false,
    maxBets: DEFAULT_MAX_BETS,
    revision: 0,
  };
  const subscribers = new Set<ToteSubscriber>();
  let batching = false;
  // Stable snapshot identity per revision so React's useSyncExternalStore
  // can consume the adapter directly.
  let cachedSnapshot: ToteSnapshot | null = null;

  function getSnapshot(): ToteSnapshot {
    if (cachedSnapshot) return cachedSnapshot;
    const lines = [...state.lines.entries()]
      .map(([key, line]) => ({
        key,
        type: line.type,
        sel: [...line.sel],
        amount: line.amount,
        oddsH: line.oddsH,
        ...(state.groups.get(key) ?? {}),
      }))
      .sort((left, right) => left.key.localeCompare(right.key));
    cachedSnapshot = Object.freeze({
      revision: state.revision,
      field: state.field,
      rtpBp: state.rtpBp,
      betType: state.betType,
      picks: Object.freeze([...state.picks]),
      lines: Object.freeze(lines),
      total: lines.reduce((sum, line) => sum + line.amount, 0),
      locked: state.locked,
      mode: "local" as const,
      maxBets: state.maxBets,
    });
    return cachedSnapshot;
  }

  function publish(reason: string) {
    if (batching) return; // one snapshot per batched mutation
    state.revision += 1;
    cachedSnapshot = null;
    if (!subscribers.size) return;
    const snapshot = getSnapshot();
    for (const listener of subscribers) {
      try {
        listener(snapshot, reason);
      } catch (error) {
        console.error("HRW tote subscriber failed.", error);
      }
    }
  }

  function weights(): number[] {
    return state.field ? state.field.horses.map((horse) => horse.weight) : [];
  }

  function priceLine(type: string, sel: number[]): number {
    const fieldWeights = weights();
    return fieldWeights.length
      ? betOddsH(fieldWeights, type, sel, state.rtpBp)
      : 0;
  }

  function metadataText(value: unknown, fallback = "") {
    return typeof value === "string" || typeof value === "number"
      ? String(value)
      : fallback;
  }

  function writeLine(
    type: string,
    sel: number[],
    amount: number,
    key: string,
    metadata: { groupId?: unknown; groupLabel?: unknown } | null = null,
  ) {
    if (amount <= 0) {
      state.lines.delete(key);
      state.groups.delete(key);
    } else {
      state.lines.set(key, { type, sel, amount, oddsH: priceLine(type, sel) });
      const groupId = metadataText(metadata?.groupId);
      if (groupId) {
        state.groups.set(key, {
          groupId,
          groupLabel: metadataText(metadata?.groupLabel, BET_TYPES[type].label),
        });
      }
    }
    publish("lines");
  }

  return {
    get betType() {
      return state.betType;
    },

    total() {
      return [...state.lines.values()].reduce(
        (sum, line) => sum + line.amount,
        0,
      );
    },

    lineCount: () => state.lines.size,

    setConfig({ rtpBp, maxBets }: { rtpBp?: number; maxBets?: number }) {
      if (rtpBp) state.rtpBp = rtpBp;
      if (maxBets) state.maxBets = maxBets;
      // Re-price kept lines whenever the pricing basis moves.
      if (rtpBp && state.field) {
        for (const line of state.lines.values()) {
          line.oddsH = priceLine(line.type, line.sel);
        }
      }
      publish("config");
    },

    setField(
      field: ToteField,
      { keepTicket = false }: { keepTicket?: boolean } = {},
    ) {
      state.field = field ? normalizeRaceField(field) : null;
      if (!keepTicket) {
        state.lines.clear();
        state.picks = [];
        state.groups.clear();
      } else if (state.field) {
        for (const line of state.lines.values()) {
          line.oddsH = priceLine(line.type, line.sel);
        }
      }
      publish("field");
    },

    getField: () => state.field,

    getLines: (): ToteLine[] =>
      [...state.lines.entries()]
        .map(([key, line]) => ({
          key,
          type: line.type,
          sel: [...line.sel],
          amount: line.amount,
          oddsH: line.oddsH,
          ...(state.groups.get(key) ?? {}),
        }))
        .sort((left, right) => left.key.localeCompare(right.key)),

    /** Most recently added draft line (Map preserves insertion order) —
     * the unambiguous pick for the cross-race Multi bridge. */
    lastLine(): ToteLine | null {
      const entry = [...state.lines.entries()].at(-1);
      if (!entry) return null;
      const [key, line] = entry;
      return {
        key,
        type: line.type,
        sel: [...line.sel],
        amount: line.amount,
        oddsH: line.oddsH,
        ...(state.groups.get(key) ?? {}),
      };
    },

    setLocked(locked: boolean) {
      state.locked = Boolean(locked);
      publish("locked");
    },

    clear() {
      state.lines.clear();
      state.picks = [];
      state.groups.clear();
      publish("lines");
    },

    getSnapshot,

    subscribe(
      listener: ToteSubscriber,
      { emitCurrent = true }: { emitCurrent?: boolean } = {},
    ) {
      if (typeof listener !== "function") {
        throw new TypeError("listener must be a function");
      }
      subscribers.add(listener);
      if (emitCurrent) listener(getSnapshot(), "subscribe");
      return () => {
        subscribers.delete(listener);
      };
    },

    commands: {
      setMarket(type: string) {
        if (!BET_TYPES[type]) return;
        state.betType = type;
        state.picks = [];
        publish("market");
      },

      toggleLine(type: string, sel: number[], amount = getChip()) {
        if (state.locked || !state.field || !BET_TYPES[type]) {
          return Promise.resolve(false);
        }
        const canonical = canonicalSel(type, sel);
        const key = ticketKey(type, canonical);
        const existing = state.lines.get(key);
        if (!existing && state.lines.size >= state.maxBets) {
          return Promise.resolve(false);
        }
        writeLine(type, canonical, existing ? 0 : amount, key);
        return Promise.resolve(!existing);
      },

      setLine(type: string, sel: number[], amount: number) {
        if (state.locked || !state.field || !BET_TYPES[type]) {
          return Promise.resolve(false);
        }
        const canonical = canonicalSel(type, sel);
        const key = ticketKey(type, canonical);
        if (
          amount > 0 &&
          !state.lines.has(key) &&
          state.lines.size >= state.maxBets
        ) {
          return Promise.resolve(false);
        }
        writeLine(type, canonical, amount, key);
        return Promise.resolve(true);
      },

      addLines(
        lines: Array<{
          type: string;
          sel: number[];
          amount: number;
          groupId?: unknown;
          groupLabel?: unknown;
        }>,
      ) {
        if (state.locked || !state.field || !Array.isArray(lines)) {
          return Promise.resolve(false);
        }
        const normalized = new Map<
          string,
          {
            type: string;
            sel: number[];
            amount: number;
            groupId?: unknown;
            groupLabel?: unknown;
          }
        >();
        for (const line of lines) {
          if (!line || !BET_TYPES[line.type] || !Array.isArray(line.sel)) {
            continue;
          }
          const sel = canonicalSel(line.type, line.sel);
          normalized.set(ticketKey(line.type, sel), {
            type: line.type,
            sel,
            amount: Number(line.amount),
            groupId: line.groupId,
            groupLabel: line.groupLabel,
          });
        }
        const additions = [...normalized.keys()].filter(
          (key) => !state.lines.has(key),
        );
        if (state.lines.size + additions.length > state.maxBets) {
          return Promise.resolve(false);
        }
        // One publish for the whole wheel: a 64-line trifecta must land as a
        // single snapshot, exactly like the donor's batched mutation.
        batching = true;
        try {
          for (const [key, line] of normalized) {
            writeLine(line.type, line.sel, line.amount, key, line);
          }
        } finally {
          batching = false;
        }
        publish("lines");
        return Promise.resolve(true);
      },
    },
  };
}
